import { AxiosResponse } from 'axios';
import { put, call, takeLatest } from 'redux-saga/effects';
import {
  DeliveryChargeRequestBody,
  DeliveryProviders,
  DeliveryQuotationDetails,
  DunzoTrackingDetails,
  getDeliveryDetails,
} from './DeliveryModels';
import {
  getDeliveryChargeSuccess,
  DeliveryActions,
  getDeliveryChargeFailed,
  getDunzoTrackingSuccess,
  getDunzoTrackingFailed,
  dunzoRunnerAssigned,
  dunzoorderCreated,
  getValetLocationFailure,
  getValetLocationSuccess,
} from './DeliveryActions';

import {
  dunzoOrderTrackingAPI,
  getBurqDeliveryQuoteAPI,
  getBurqDeliveryStatusAPI,
  getDeliveryChargeAPI,
  getValetLocation,
} from './DeliveryAPI';
import {
  GET_DELIVERY_CHARGE_INITATED,
  GET_DUNZO_TRACKING_INTIATED,
  GET_VALET_LOCATION,
} from './DeliveryConstants';
import { LocationState } from '../location/locationModels';
import { decryptJson } from '../../utils/react-ec-utils';
import { validateAndParseResponse } from '../../utils/hmac-utils';
import { showErrorToast, showErrorToastDesktop } from '../../utils/toastUtils';

/**
 * Prices a Burq delivery from the location's own distance bands.
 *
 * v3 answers in plain JSON rather than the gateway's signed envelope, so this
 * does not go through `validateAndParseResponse`.
 *
 * Three outcomes, and they are not the same thing:
 *
 * - **priced** — a fee for the distance, stored as the quotation
 * - **not deliverable** — the address is beyond the furthest band. A normal
 *   answer, not an error, and the diner is told the distance so they know why
 * - **failed** — no bands configured, or the distance could not be measured
 *
 * The last two both end as `getDeliveryChargeFailed`, because the checkout has
 * one notion of "no delivery price"; they differ only in what the diner reads.
 */
function* quoteViaBurqBands(payload: DeliveryChargeRequestBody, isMobile: boolean): any {
  const tell = (message: string) =>
    isMobile ? showErrorToast(message) : showErrorToastDesktop(message);

  try {
    const result = yield call(getBurqDeliveryQuoteAPI, payload);

    if (!result?.ok) {
      // v3 names the reason — no bands configured, distance unavailable — and
      // it is more use in the log than in front of a diner.
      // eslint-disable-next-line no-console
      console.warn('Burq delivery quote failed', result?.status, result?.data);
      tell('We could not work out a delivery fee for this address. Please try again.');
      yield put(getDeliveryChargeFailed());
      return;
    }

    const quote = result.data;

    if (quote?.deliverable === false) {
      tell(
        quote?.message ||
          `Sorry, we do not deliver that far${
            quote?.maxDeliveryMiles ? ` — up to ${quote.maxDeliveryMiles} miles` : ''
          }.`,
      );
      yield put(getDeliveryChargeFailed());
      return;
    }

    if (typeof quote?.fee !== 'number') {
      tell('We could not work out a delivery fee for this address. Please try again.');
      yield put(getDeliveryChargeFailed());
      return;
    }

    // Normalised to the shape every existing consumer already reads, so
    // nothing downstream has to know which provider priced it.
    yield put(
      getDeliveryChargeSuccess({
        estimatedPrice: String(quote.fee),
      } as DeliveryQuotationDetails),
    );
  } catch (err) {
    yield put(getDeliveryChargeFailed());
  }
}

function* getDeliveryCharge(action: DeliveryActions): any {
  const isMobile = window.innerWidth <= 768;
  const deliveryChargePayload = action.payload as DeliveryChargeRequestBody;

  // A Burq location is priced by v3 from its own distance bands, not by asking
  // the courier. Burq quotes asynchronously — the price arrives by webhook
  // minutes later — so there is nothing to ask at the moment a diner picks an
  // address. Every other provider still goes to the gateway unchanged.
  if (String(deliveryChargePayload?.deliveryProviderId ?? '').trim() === DeliveryProviders.BURQ) {
    yield call(quoteViaBurqBands, deliveryChargePayload, isMobile);
    return;
  }

  try {
    let parsedResponse;
    const response: AxiosResponse = yield call(
      getDeliveryChargeAPI,
      deliveryChargePayload
    );
    parsedResponse = yield call(validateAndParseResponse, response.data);
    if (!parsedResponse?.valid) {
      if (isMobile) {
        showErrorToast(parsedResponse?.message || 'Failed to update profile, Please try Again');
      } else {
        showErrorToastDesktop(
          parsedResponse?.message || 'Failed to update profile, Please try Again'
        );
      }
      return;
    }
    // const decryptedData = decryptJson(response.data.encryptedText);
    const decryptedData = response.data.data;
    if (response.status === 200 && decryptedData.estimatedPrice >= 0) {
      yield put(getDeliveryChargeSuccess(decryptedData as DeliveryQuotationDetails));
    } else {
      yield put(getDeliveryChargeFailed());
    }
  } catch (err: any) {
    yield put(getDeliveryChargeFailed());
  }
}

/**
 * Tracking for a Burq delivery.
 *
 * `/delivery/status/:orderId` on v3 rather than the gateway's tracking call —
 * see `getBurqDeliveryStatusAPI` for why the gateway cannot answer for Burq.
 *
 * Mapped onto the same `DunzoTrackingDetails` shape every tracking consumer
 * already reads, so the "Track your delivery partner" link works unchanged. The
 * name is historical; the shape is the app's general tracking contract.
 *
 * Only dispatched once the courier has a tracking URL. Before that there is
 * nothing to open, and an empty link is worse than none.
 */
function* trackBurqDelivery(orderId: string): any {
  try {
    const result = yield call(getBurqDeliveryStatusAPI, orderId);

    // 404 means no delivery is arranged — a pickup order, or one not yet
    // dispatched. Silent on purpose: the tracker polls, and a diner should not
    // be shown an error for an order that simply has no courier.
    if (!result?.ok || !result?.data?.trackUrl) {
      yield put(getDunzoTrackingFailed());
      return;
    }

    yield put(
      getDunzoTrackingSuccess({
        taskId: result.data.orderId,
        trackingUrl: result.data.trackUrl,
        // `eta` must be an OBJECT, not absent. The tracker reads
        // `dunzoTrackingDetails?.eta.dropoff` — optional on the outer value but
        // not on `eta` — so omitting it threw and the error boundary took the
        // whole tracker page down. Nulls inside are handled; a missing `eta` is
        // not.
        eta: { pickup: null, dropoff: null },
        // Burq reports the driver by webhook and we do not store it yet, so
        // `runner` is left absent rather than faked. Nothing dereferences it
        // without a guard. The link is what the diner actually taps.
      } as unknown as DunzoTrackingDetails),
    );
  } catch (err) {
    yield put(getDunzoTrackingFailed());
  }
}

function* getDunzotrackingDetails(action: DeliveryActions): any {
  const isMobile = window.innerWidth <= 768;

  const trackingRequest = action.payload as getDeliveryDetails;
  if (String(trackingRequest?.DeliveryProviders ?? '').trim() === DeliveryProviders.BURQ) {
    yield call(trackBurqDelivery, String(trackingRequest.orderId ?? ''));
    return;
  }

  try {
    let parsedResponse;
    const response: AxiosResponse = yield call(
      dunzoOrderTrackingAPI,
      action.payload as getDeliveryDetails
    );
    parsedResponse = yield call(validateAndParseResponse, response.data);

    if (!parsedResponse?.valid) {
      if (isMobile) {
        showErrorToast(parsedResponse?.message || 'Failed to update profile, Please try Again');
      } else {
        showErrorToastDesktop(
          parsedResponse?.message || 'Failed to update profile, Please try Again'
        );
      }
      return;
    }
    if (response.status === 200) {
      // const decryptData = decryptJson(response.data.encryptedText);
      const decryptData = response.data.data;
      if (decryptData.runner && decryptData.trackingUrl) {
        yield put(getDunzoTrackingSuccess(decryptData as DunzoTrackingDetails));
      } else if (decryptData.runner) {
        yield put(dunzoRunnerAssigned(decryptData as DunzoTrackingDetails));
      } else if (decryptData.eta) {
        yield put(dunzoorderCreated(decryptData as DunzoTrackingDetails));
      } else {
        yield put(getDunzoTrackingFailed());
      }
    } else {
      yield put(getDunzoTrackingFailed());
    }
  } catch (err) {
    yield put(getDunzoTrackingFailed());
  }
}

// Instore Valet app tracking
function* getDeliveryValetLocation(action: DeliveryActions) {
  try {
    const response: AxiosResponse = yield call(getValetLocation, action.payload as string);
    if (response.status === 200 && response.data.delivery_staff_id) {
      let data = {
        lat: response.data.latitude,
        lng: response.data.longitude,
      } as LocationState;
      yield put(getValetLocationSuccess(data));
    } else {
      yield put(getValetLocationFailure());
    }
  } catch (err) {
    yield put(getValetLocationFailure());
  }
}

export default function* deliverySaga() {
  yield takeLatest(GET_DELIVERY_CHARGE_INITATED, getDeliveryCharge);
  yield takeLatest(GET_DUNZO_TRACKING_INTIATED, getDunzotrackingDetails);
  yield takeLatest(GET_VALET_LOCATION, getDeliveryValetLocation);
}
